<div class="import-dialog-container">
  <!-- Modal Header -->
  <div class="modal-header">
    <h2 class="modal-title">{{ dialogTitle }}</h2>
    <button
      mat-icon-button
      class="close-button"
      (click)="cancel()"
      aria-label="Close"
    >
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <!-- Modal Content -->
  <div class="modal-content">
    <!-- Error message display -->
    @if (error()) {
      <div class="error-message">
        <mat-icon>error</mat-icon>
        <span>{{ error() }}</span>
      </div>
    }

    <!-- File Selection Stage -->
    @if (currentStage() === ImportStage.FileSelection) {
      <div class="file-upload-container">
        <div
          class="drop-zone"
          [class.has-file]="selectedFile"
          tabindex="0"
          role="button"
          aria-label="Drop export file or browse"
          (click)="fileInput.click()"
          (keydown.enter)="fileInput.click()"
          (keydown.space)="$event.preventDefault()"
          (keyup.space)="fileInput.click(); $event.preventDefault()"
          (dragover)="onDragOver($event)"
          (drop)="onDrop($event)"
        >
          <input
            type="file"
            #fileInput
            (change)="onFileSelected($event)"
            accept=".zip"
            hidden
          />
          @if (!selectedFile) {
            <mat-icon class="upload-icon">cloud_upload</mat-icon>
            <p>
              Drop export file
              <button
                type="button"
                class="browse-link"
                (click)="$event.stopPropagation(); fileInput.click()"
              >
                or browse
              </button>
            </p>
            <p class="file-hint">
              Only .zip project export files are supported
            </p>
          }
          @if (selectedFile) {
            <div class="selected-file">
              <mat-icon>insert_drive_file</mat-icon>
              <span>{{ selectedFile.name }}</span>
              <button
                mat-icon-button
                aria-label="Remove selected file"
                (click)="$event.stopPropagation(); removeFile()"
              >
                <mat-icon>close</mat-icon>
              </button>
            </div>
          }
        </div>
      </div>
    }

    <!-- Analyzing Stage -->
    @if (currentStage() === ImportStage.Analyzing) {
      <div class="analyzing-container">
        <mat-progress-spinner mode="indeterminate" diameter="80">
        </mat-progress-spinner>
        <p class="analysis-status">Analyzing Export File</p>
        <p class="analysis-description">
          Reading project versions and structure...
        </p>
      </div>
    }

    <!-- Importing Stage -->
    @if (currentStage() === ImportStage.Importing) {
      <div class="analyzing-container">
        <mat-progress-spinner mode="indeterminate" diameter="80">
        </mat-progress-spinner>
        <p class="analysis-status">Importing Project</p>
        <p class="analysis-description">
          Please wait while the project is being imported...
        </p>
      </div>
    }

    <!-- Import Complete Stage (shown when there are warnings) -->
    @if (currentStage() === ImportStage.Complete) {
      <div class="import-complete-container">
        <mat-icon class="success-icon">check_circle</mat-icon>
        <p class="success-message">Import completed with warnings</p>
        @if (warnings().length > 0) {
          <div class="warnings-list">
            <h4>Warnings:</h4>
            <ul>
              @for (warning of warnings(); track $index) {
                <li>{{ warning }}</li>
              }
            </ul>
          </div>
        }
      </div>
    }

    <!-- Project Details Stage -->
    @if (currentStage() === ImportStage.ProjectDetails) {
      <div class="project-details-container">
        <form [formGroup]="importForm">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Project Name</mat-label>
            <input matInput formControlName="projectName" required />
            @if (importForm.get("projectName")?.hasError("required")) {
              <mat-error> Project name is required </mat-error>
            }
            @if (importForm.get("projectName")?.hasError("notUnique")) {
              <mat-error> A project with this name already exists. Please choose a different name. </mat-error>
            }
          </mat-form-field>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Version to Import</mat-label>
            <mat-select
              [value]="selectedVersionId()"
              (selectionChange)="onVersionChange($event.value)"
              required
            >
              @for (version of availableVersions(); track $index) {
                <mat-option [value]="getVersionId(version)">
                  {{ getVersionDisplayName(version) }}
                </mat-option>
              }
            </mat-select>
            <mat-hint
              >Select which version from the export file to import</mat-hint
            >
            @if (!selectedVersionId()) {
              <mat-error> Please select a version to import </mat-error>
            }
          </mat-form-field>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Password</mat-label>
            <input
              matInput
              [type]="hidePassword ? 'password' : 'text'"
              formControlName="password"
              required
            />
            <button
              mat-icon-button
              matSuffix
              (click)="hidePassword = !hidePassword"
              [attr.aria-label]="'Hide password'"
              [attr.aria-pressed]="hidePassword"
            >
              <mat-icon>{{
                hidePassword ? "visibility_off" : "visibility"
              }}</mat-icon>
            </button>
            <mat-hint>Password used to decrypt the project export</mat-hint>
            @if (importForm.get("password")?.hasError("required")) {
              <mat-error> Password is required </mat-error>
            }
          </mat-form-field>
          <!-- Version Information -->
          @if (selectedVersionId() && availableVersions().length > 0) {
            <div class="version-info">
              <h4>Selected Version Details</h4>
              @for (version of availableVersions(); track $index) {
                @if (getVersionId(version) === selectedVersionId()) {
                  <div class="version-details">
                    <p>
                      <strong>Version:</strong>
                      {{ getMajorVersion(version) }}.{{
                        getMinorVersion(version)
                      }}
                    </p>
                    <p>
                      <strong>Created:</strong>
                      {{ getCreatedAt(version) | serverDate: "medium" }}
                    </p>
                    @if (version.notebooks) {
                      <p>
                        <strong>Workflows:</strong>
                        {{ version.notebooks.length }}
                      </p>
                    }
                  </div>
                }
              }
            </div>
          }
        </form>
      </div>
    }
  </div>

  <!-- Modal Actions -->
  <div class="modal-actions">
    @if (currentStage() !== ImportStage.Importing) {
      <button class="cancel-button" (click)="cancel()">Cancel</button>
    }

    @if (currentStage() === ImportStage.FileSelection) {
      <button
        class="analyze-button"
        [disabled]="!selectedFile"
        (click)="uploadAndAnalyze()"
      >
        Analyze File
      </button>
    }

    @if (currentStage() === ImportStage.ProjectDetails) {
      <button
        class="import-button"
        [disabled]="importForm.invalid || !selectedVersionId()"
        (click)="confirmImport()"
      >
        Import Project
      </button>
    }

    @if (currentStage() === ImportStage.Complete) {
      <button class="copy-button" (click)="copyWarnings()">
        Copy Warnings
      </button>
      <button class="import-button" (click)="cancel()">
        Close
      </button>
    }
  </div>
</div>
